import { memo, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { filter, pipe, take } from 'remeda';
import type { UserInfo, UserCourse } from '../../services/userManager';
import {
  Card,
  Group,
  Text,
  Title,
  Stack,
  Badge,
  Grid,
  Button,
  ThemeIcon,
} from '@mantine/core';
import {
  Bug,
  ChartNoAxesCombined,
  ChevronLeft,
  Clipboard,
  Glasses,
  GraduationCap,
  Music,
  Pencil,
  ShieldCheck,
  Store,
  Trash2,
} from 'lucide-react';
import { useAuthUser, useHasRole } from '../../providers/AuthProvider';
import {
  canAccessLegacyUsersAdmin,
  canDeleteAdminUser,
} from '../../config/env';
import { professions } from '../../data/professions';
import { discipline } from '../../data/discipline';
import { getLegacySourceBadge } from './legacySourceBadge';

interface Product {
  product_id?: number;
  id?: number;
  name: string;
  category?: string;
  experience_category?: string;
  until_date?: string;
  [key: string]: unknown;
}

interface UserDashboardProps {
  userInfo: UserInfo | null;
  userProducts: Product[];
  userCourses: UserCourse[];
  hasManageProducts: boolean;
  hasManageCourses: boolean;
  hasManagePermissions: boolean;
  hasManageDevices: boolean;
  hasDev: boolean;
  hasAnalytics: boolean;
  /** When true, hide live admin quick-actions (no real be2025 user id). */
  previewMode?: boolean;
  backTo?: string;
  backLabel?: string;
  onEditUser?: () => void;
  onDeleteUser?: () => void;
  deletingUser?: boolean;
}

type ProfileRow = {
  key: string;
  label: string;
  render?: (user: UserInfo) => ReactNode;
};

const professionLabelById = new Map(
  professions.map(item => [Number(item.value), item.label])
);

const resolveProfessionLabel = (value: unknown): string => {
  if (value == null || value === '') return '—';
  const id = Number(value);
  if (!Number.isFinite(id)) return String(value);
  const label = professionLabelById.get(id);
  return label ? `${label} (${id})` : String(id);
};

const resolveDiscipleLabel = (
  discipleValue: unknown,
  professionValue: unknown
): string => {
  if (discipleValue == null || discipleValue === '') return '—';
  const discipleId = Number(discipleValue);
  if (!Number.isFinite(discipleId)) return String(discipleValue);

  const professionId = Number(professionValue);
  const lists: Array<{ label: string; value: number }> = [];
  if (Number.isFinite(professionId) && discipline[String(professionId)]) {
    lists.push(...discipline[String(professionId)]);
  } else {
    for (const key of Object.keys(discipline)) {
      lists.push(...(discipline[key] ?? []));
    }
  }

  const match = lists.find(item => Number(item.value) === discipleId);
  return match ? `${match.label} (${discipleId})` : String(discipleId);
};

/** Single table: be2025 Users (= v1 BES Users + user_info). */
const PROFILE_ROWS: ProfileRow[] = [
  { key: 'id', label: 'ID (be2025)' },
  { key: 'email', label: 'Email' },
  { key: 'type', label: 'Tipo' },
  { key: 'first_name', label: 'Nome' },
  { key: 'last_name', label: 'Cognome' },
  { key: 'full_name', label: 'Nome completo' },
  { key: 'title', label: 'Titolo' },
  { key: 'role', label: 'Ruolo' },
  {
    key: 'profession',
    label: 'Professione',
    render: user => resolveProfessionLabel(user.profession),
  },
  {
    key: 'disciple',
    label: 'Disciplina',
    render: user => resolveDiscipleLabel(user.disciple, user.profession),
  },
  { key: 'contract', label: 'Contratto' },
  { key: 'fiscal_code', label: 'Codice fiscale' },
  { key: 'order_of_psychologists', label: 'Ordine psicologi' },
  { key: 'order_number', label: 'N. ordine' },
  { key: 'region', label: 'Regione' },
  { key: 'address', label: 'Indirizzo' },
  { key: 'vat_number', label: 'P. IVA' },
  { key: 'sdi_number', label: 'SDI' },
  { key: 'website', label: 'Website' },
  { key: 'is_public', label: 'Pubblico' },
  { key: 'belongs_to_enterprise', label: 'Enterprise id' },
  { key: 'stripe_id', label: 'Stripe id' },
  { key: 'profile_photo_path', label: 'Foto profilo' },
  { key: 'legacy_source', label: 'Legacy source' },
  {
    key: 'legacy_user_id',
    label: 'Legacy user id (vapor)',
    render: user =>
      user.legacy_user_id == null ? '—' : `#${String(user.legacy_user_id)}`,
  },
  { key: 'created_at', label: 'Data registrazione' },
  { key: 'updated_at', label: 'Ultimo aggiornamento' },
];

const formatProfileValue = (value: unknown): string => {
  if (value == null || value === '') return '—';
  if (value instanceof Date) {
    return value.toLocaleString('it-IT');
  }
  if (typeof value === 'string') {
    const asDate = new Date(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(value) && !Number.isNaN(asDate.getTime())) {
      return asDate.toLocaleString('it-IT');
    }
    return value;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export const UserDashboard = memo<UserDashboardProps>(
  ({
    userInfo,
    userProducts,
    userCourses,
    hasManageProducts,
    hasManageCourses,
    hasManagePermissions,
    hasManageDevices,
    hasDev,
    hasAnalytics,
    previewMode = false,
    backTo = '/admin/users',
    backLabel = 'Torna alla Lista Utenti',
    onEditUser,
    onDeleteUser,
    deletingUser = false,
  }) => {
    const { id } = useParams<{ id: string }>();
    const authUser = useAuthUser();
    const legacyAdmin = canAccessLegacyUsersAdmin(authUser?.email);
    const isAdmin = useHasRole('admin');
    const isModerator = useHasRole('moderator');
    const canManageUser = !previewMode && (isAdmin || isModerator);
    const canDeleteUser =
      canManageUser && canDeleteAdminUser(authUser?.email);

    const completedCourses = pipe(
      userCourses,
      filter(course => Boolean(course.completed_at))
    );
    const inProgressCourses = pipe(
      userCourses,
      filter(course => !course.completed_at && course.current_time > 0)
    );
    const activeProducts = pipe(
      userProducts,
      filter(
        product =>
          !product.until_date || new Date(product.until_date) > new Date()
      )
    );

    return (
      <Stack gap='lg'>
        {/* Back to Users List */}
        <Group>
          <Button
            variant='subtle'
            leftSection={<ChevronLeft size={16} />}
            component={Link}
            to={backTo}
          >
            {backLabel}
          </Button>
        </Group>

        {/* User Summary Cards */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder>
              <Group>
                <ThemeIcon size='lg' variant='light' color='blue'>
                  <Store size={16} />
                </ThemeIcon>
                <div>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                    Prodotti Attivi
                  </Text>
                  <Text fw={700} size='xl'>
                    {activeProducts.length}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder>
              <Group>
                <ThemeIcon size='lg' variant='light' color='green'>
                  <GraduationCap size={16} />
                </ThemeIcon>
                <div>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                    Corsi Completati
                  </Text>
                  <Text fw={700} size='xl'>
                    {completedCourses.length}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder>
              <Group>
                <ThemeIcon size='lg' variant='light' color='orange'>
                  <GraduationCap size={16} />
                </ThemeIcon>
                <div>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                    Corsi in Corso
                  </Text>
                  <Text fw={700} size='xl'>
                    {inProgressCourses.length}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder>
              <Group>
                <ThemeIcon size='lg' variant='light' color='purple'>
                  <Glasses size={16} />
                </ThemeIcon>
                <div>
                  <Text size='xs' c='dimmed' tt='uppercase' fw={700}>
                    Dispositivi
                  </Text>
                  <Text fw={700} size='xl'>
                    -
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Complete User Information */}
        <Card withBorder>
          <Stack gap='md'>
            <Group justify='space-between' align='center' wrap='wrap'>
              <div>
                <Title order={3}>Informazioni utente</Title>
                <Text size='sm' c='dimmed'>
                  Profilo be2025{' '}
                  <Text span ff='monospace'>
                    Users
                  </Text>{' '}
                  (ex v1 BES Users + user_info)
                </Text>
              </div>
              <Group gap='xs'>
                {canManageUser ? (
                  <Button
                    variant='light'
                    size='xs'
                    leftSection={<Pencil size={14} />}
                    onClick={onEditUser}
                    disabled={!userInfo || !onEditUser}
                  >
                    Modifica
                  </Button>
                ) : null}
                {canDeleteUser ? (
                  <Button
                    variant='light'
                    color='red'
                    size='xs'
                    leftSection={<Trash2 size={14} />}
                    onClick={onDeleteUser}
                    loading={deletingUser}
                    disabled={!userInfo || !onDeleteUser}
                  >
                    Elimina
                  </Button>
                ) : null}
                {userInfo?.legacy_source ? (
                  <>
                    {(() => {
                      const badge = getLegacySourceBadge(userInfo.legacy_source);
                      if (!badge) return null;
                      return (
                        <Badge size='lg' variant='filled' color={badge.color}>
                          {badge.label}
                        </Badge>
                      );
                    })()}
                    {legacyAdmin && userInfo.legacy_user_id != null ? (
                      <Button
                        component={Link}
                        to={`/admin/legacy-users/${userInfo.legacy_user_id}`}
                        variant='light'
                        size='xs'
                        color='orange'
                      >
                        vapor #{String(userInfo.legacy_user_id)}
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </Group>
            </Group>

            {!userInfo ? (
              <Text c='dimmed' size='sm'>
                Nessun dato utente
              </Text>
            ) : (
              <Grid>
                {PROFILE_ROWS.map(row => (
                  <Grid.Col key={row.key} span={{ base: 12, sm: 6, md: 4 }}>
                    <Stack gap={2}>
                      <Text fw={600} size='sm'>
                        {row.label}
                      </Text>
                      <Text size='xs' c='dimmed' ff='monospace'>
                        {row.key}
                      </Text>
                      {row.render ? (
                        row.render(userInfo)
                      ) : (
                        <Text size='sm' style={{ wordBreak: 'break-word' }}>
                          {formatProfileValue(userInfo[row.key])}
                        </Text>
                      )}
                    </Stack>
                  </Grid.Col>
                ))}
              </Grid>
            )}
          </Stack>
        </Card>

        {/* Quick Actions — hidden in migration preview (no real be2025 user) */}
        {!previewMode ? (
        <Card withBorder>
          <Stack gap='md'>
            <Title order={3}>Azioni Rapide</Title>

            <Group gap='xs' wrap='wrap'>
              {hasManageProducts && (
                <Button
                  variant='light'
                  leftSection={<Store size={16} />}
                  component={Link}
                  to={`/admin/users/${id}/products`}
                >
                  Gestisci Prodotti
                </Button>
              )}

              {hasManageProducts && (
                <Button
                  variant='light'
                  leftSection={<Music size={16} />}
                  component={Link}
                  to={`/admin/users/${id}/audios`}
                >
                  Gestione audio personalizzati
                </Button>
              )}

              {hasManageCourses && (
                <Button
                  variant='light'
                  leftSection={<GraduationCap size={16} />}
                  component={Link}
                  to={`/admin/users/${id}/courses`}
                >
                  Gestisci Corsi
                </Button>
              )}

              {hasManageDevices && (
                <Button
                  variant='light'
                  leftSection={<Glasses size={16} />}
                  component={Link}
                  to={`/admin/users/${id}/devices`}
                >
                  Gestisci Dispositivi
                </Button>
              )}

              {hasManageDevices && (
                <Button
                  variant='light'
                  leftSection={<Clipboard size={16} />}
                  component={Link}
                  to={`/admin/users/${id}/logs`}
                >
                  Log dispositivi
                </Button>
              )}

              {hasManagePermissions && (
                <Button
                  variant='light'
                  leftSection={<ShieldCheck size={16} />}
                  disabled
                >
                  Gestisci Permessi
                </Button>
              )}

              {hasDev && (
                <Button
                  variant='light'
                  leftSection={<Bug size={16} />}
                  disabled
                >
                  Error Log
                </Button>
              )}

              {hasAnalytics && (
                <Button
                  variant='light'
                  leftSection={<ChartNoAxesCombined size={16} />}
                  disabled
                >
                  Analytics
                </Button>
              )}
            </Group>
          </Stack>
        </Card>
        ) : null}

        {/* Recent Activity Summary */}
        <Card withBorder>
          <Stack gap='md'>
            <Title order={3}>Riepilogo Attività</Title>

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap='xs'>
                  <Text size='sm' fw={500}>
                    Prodotti Attivi
                  </Text>
                  {activeProducts.length > 0 ? (
                    <Stack gap='xs'>
                      {pipe(activeProducts, take(3)).map(product => (
                        <Group
                          key={product.product_id || product.id}
                          justify='space-between'
                        >
                          <Text size='sm'>{product.name}</Text>
                          <Badge size='xs' color='green'>
                            Attivo
                          </Badge>
                        </Group>
                      ))}
                      {activeProducts.length > 3 && (
                        <Text size='xs' c='dimmed'>
                          +{activeProducts.length - 3} altri prodotti
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Text size='sm' c='dimmed'>
                      Nessun prodotto attivo
                    </Text>
                  )}
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap='xs'>
                  <Text size='sm' fw={500}>
                    Corsi Recenti
                  </Text>
                  {userCourses.length > 0 ? (
                    <Stack gap='xs'>
                      {pipe(userCourses, take(3)).map(course => (
                        <Group key={course.courseId} justify='space-between'>
                          <Text size='sm'>{course.courseName}</Text>
                          <Badge
                            size='xs'
                            color={
                              course.completed_at
                                ? 'green'
                                : course.current_time > 0
                                  ? 'blue'
                                  : 'gray'
                            }
                          >
                            {course.completed_at
                              ? 'Completato'
                              : course.current_time > 0
                                ? 'In corso'
                                : 'Non iniziato'}
                          </Badge>
                        </Group>
                      ))}
                      {userCourses.length > 3 && (
                        <Text size='xs' c='dimmed'>
                          +{userCourses.length - 3} altri corsi
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Text size='sm' c='dimmed'>
                      Nessun corso assegnato
                    </Text>
                  )}
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      </Stack>
    );
  }
);

UserDashboard.displayName = 'UserDashboard';
